import * as Sentry from '@sentry/react';
import {
  FieldTextAreaCompact,
  FieldTextCompact,
  Pushbutton
} from '@stratumn/atomic';
import to from 'await-to-js';
import graphql from 'babel-plugin-relay/macro';
import { LeftPane } from 'components/layout';
import { UploadPhoto } from 'components/ui';
import { ROUTE_BOT } from 'constant/routes';
import compose from 'lodash.flowright';
import { updateBot } from 'mutations';
import PropType from 'prop-types';
import { useState } from 'react';
import injectSheet from 'react-jss';
import { createFragmentContainer } from 'react-relay';
import { generatePath, Link, withRouter } from 'react-router-dom';
import { SpanType, withSpanAsync } from 'tracing';
import { withGqlClient, withRestClient } from 'wrappers';

import styles from './editBotProfile.style';

export const EditBotProfile = ({
  classes,
  environment,
  client,
  bot,
  history
}) => {
  const [submitted, setSubmit] = useState(false);
  const [name, setName] = useState(bot.name || '');
  const [description, setDescription] = useState(bot.description || '');
  const [avatar, setAvatar] = useState(bot.avatar || '');
  const { organization } = bot;

  const onSubmit = () => {
    withSpanAsync('updateBot', SpanType.processing, async () => {
      setSubmit(true);

      let avatarUrl;

      if (avatar instanceof File) {
        const { digest } = await client.media.uploadFile(avatar, {
          disableEncryption: true
        });
        avatarUrl = await client.media.buildDownloadURL(digest);
      }

      const [err] = await to(
        updateBot(environment, bot.rowId, name, description, avatarUrl)
      );

      if (err) {
        Sentry.captureException(err);
        setSubmit(false);
      } else {
        gotToBotProfilePage();
      }
    });
  };

  const gotToBotProfilePage = () => {
    history.push(
      generatePath(ROUTE_BOT, {
        id: bot.rowId,
        orgId: organization.rowId
      })
    );
  };

  return (
    <LeftPane title="Edit Bot">
      <div className={classes.formWrapper}>
        <div className={classes.avatarWrapper}>
          <UploadPhoto
            avatar={JSON.stringify(avatar)}
            getNewAvatar={setAvatar}
            overlay
            size={56}
            user
          />
        </div>

        <div className={classes.fieldWrapper}>
          <FieldTextCompact
            data-testid="bot-name"
            label="Bot name"
            onValueChange={e => setName(e.target.value)}
            value={name}
          />
        </div>
        <div className={classes.fieldWrapper}>
          <FieldTextAreaCompact
            data-testid="bot-description"
            label="Bot description"
            noResize
            onValueChange={e => setDescription(e.target.value)}
            rows={4}
            value={description}
          />
        </div>
        <div className={classes.formBtnWrapper}>
          <div className={classes.formBtnLeft}>
            <Link
              style={{ textDecoration: 'none' }}
              to={generatePath(ROUTE_BOT, {
                id: bot.rowId,
                orgId: organization.rowId
              })}
            >
              <Pushbutton>cancel</Pushbutton>
            </Link>
          </div>
          <Pushbutton
            data-testid="save-button"
            disabled={!name || submitted}
            onClick={onSubmit}
            primary
          >
            save
          </Pushbutton>
        </div>
      </div>
    </LeftPane>
  );
};

EditBotProfile.propTypes = {
  classes: PropType.object.isRequired,
  environment: PropType.object.isRequired,
  client: PropType.object.isRequired,
  history: PropType.object.isRequired,
  bot: PropType.object.isRequired
};

export default createFragmentContainer(
  compose(
    withRouter,
    withGqlClient,
    withRestClient,
    injectSheet(styles)
  )(EditBotProfile),
  {
    bot: graphql`
      fragment editBotProfile_bot on Bot {
        id
        rowId
        name
        description
        avatar
        organization {
          id
          rowId
          name
          canUpdate
        }
      }
    `
  }
);
